/**
 * AutoFix Utility - Centralized Keywords Auto-Fix Logic
 * 
 * This utility provides all autofix functionality for keywords management.
 * Used by both individual file cards and batch operations to ensure consistency.
 * 
 * Features:
 * - Duplicate detection with sophisticated logic
 * - Auto-fix algorithm for microstock optimization
 * - Helper functions for keyword normalization
 * - Stop words filtering for better SEO
 * - Performance optimizations with caching
 * - Configurable similarity thresholds
 * - Extensible for future enhancements
 */

// Performance optimization: Caching for expensive operations
const similarityCache = new Map<string, number>();
const normalizedCache = new Map<string, string>();
const singularizedCache = new Map<string, string>();
const splitCompoundCache = new Map<string, string[]>();

// Performance settings
const PERFORMANCE_CONFIG = {
  MAX_CACHE_SIZE: 10000, // Maximum cache entries
  SIMILARITY_THRESHOLD: 0.3, // Skip comparisons below this threshold
  EARLY_EXIT_THRESHOLD: 0.1, // Stop processing if similarity is too low
  BATCH_SIZE: 1000 // Process large keyword lists in batches
};

// Cache management
export const clearCache = () => {
  similarityCache.clear();
  normalizedCache.clear();
  singularizedCache.clear();
  splitCompoundCache.clear();
};

export const getCacheStats = () => {
  return {
    similarityCache: similarityCache.size,
    normalizedCache: normalizedCache.size,
    singularizedCache: singularizedCache.size,
    splitCompoundCache: splitCompoundCache.size,
    totalCacheSize: similarityCache.size + normalizedCache.size + singularizedCache.size + splitCompoundCache.size
  };
};

const manageCacheSize = (cache: Map<string, any>) => {
  if (cache.size > PERFORMANCE_CONFIG.MAX_CACHE_SIZE) {
    const entries = Array.from(cache.entries());
    const toDelete = entries.slice(0, entries.length - PERFORMANCE_CONFIG.MAX_CACHE_SIZE);
    toDelete.forEach(([key]) => cache.delete(key));
  }
};

/**
 * Process large keyword lists in batches for better performance
 */
export const processKeywordsInBatches = async <T>(
  keywords: string[],
  processor: (batch: string[]) => T,
  batchSize: number = PERFORMANCE_CONFIG.BATCH_SIZE
): Promise<T[]> => {
  const results: T[] = [];
  
  for (let i = 0; i < keywords.length; i += batchSize) {
    const batch = keywords.slice(i, i + batchSize);
    const result = processor(batch);
    results.push(result);
    
    // Allow other operations to run (non-blocking)
    if (i + batchSize < keywords.length) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }
  
  return results;
};

// Stop words that should not be individual keywords
const STOP_WORDS = new Set([
  'a', 'about', 'above', 'after', 'again', 'against', 'all', 'am', 'an', 'and', 'any', 'are', 'aren\'t', 'as', 'at',
  'be', 'because', 'been', 'before', 'being', 'below', 'between', 'both', 'but', 'by',
  'can', 'can\'t', 'cannot', 'could', 'couldn\'t',
  'did', 'didn\'t', 'do', 'does', 'doesn\'t', 'doing', 'don\'t', 'down', 'during',
  'each', 'few', 'for', 'from', 'further',
  'had', 'hadn\'t', 'has', 'hasn\'t', 'have', 'haven\'t', 'having', 'he', 'he\'d', 'he\'ll', 'he\'s', 'her', 'here', 'here\'s', 'hers', 'herself', 'him', 'himself', 'his', 'how', 'how\'s',
  'i', 'i\'d', 'i\'ll', 'i\'m', 'i\'ve', 'if', 'in', 'into', 'is', 'isn\'t', 'it', 'it\'s', 'its', 'itself',
  'let\'s',
  'me', 'more', 'most', 'mustn\'t', 'my', 'myself',
  'no', 'nor', 'not',
  'of', 'off', 'on', 'once', 'only', 'or', 'other', 'ought', 'our', 'ours', 'ourselves', 'out', 'over', 'own',
  'same', 'shan\'t', 'she', 'she\'d', 'she\'ll', 'she\'s', 'should', 'shouldn\'t', 'so', 'some', 'such',
  'than', 'that', 'that\'s', 'the', 'their', 'theirs', 'them', 'themselves', 'then', 'there', 'there\'s', 'these', 'they', 'they\'d', 'they\'ll', 'they\'re', 'they\'ve', 'this', 'those', 'through', 'to', 'too', 'under', 'until', 'up',
  'very',
  'was', 'wasn\'t', 'we', 'we\'d', 'we\'ll', 'we\'re', 'we\'ve', 'were', 'weren\'t', 'what', 'what\'s', 'when', 'when\'s', 'where', 'where\'s', 'which', 'while', 'who', 'who\'s', 'whom', 'why', 'why\'s', 'with', 'won\'t', 'would', 'wouldn\'t',
  'you', 'you\'d', 'you\'ll', 'you\'re', 'you\'ve', 'your', 'yours', 'yourself', 'yourselves'
]);

export interface DuplicateGroup {
  indices: number[];
  keywords: string[];
  type: 'exact' | 'contains' | 'plural' | 'compound';
  confidence: number;
}

export interface AutoFixOptions {
  maxKeywords?: number;
  strictMode?: boolean;
  preserveOrder?: boolean;
  filterStopWords?: boolean;
}

export interface AutoFixResult {
  fixedKeywords: string[];
  removedCount: number;
  addedCount: number;
  duplicateGroups: DuplicateGroup[];
}

/**
 * Normalize keyword for comparison (case-insensitive, trimmed)
 * Uses caching for performance optimization
 */
export const normalizeKeyword = (keyword: string): string => {
  if (normalizedCache.has(keyword)) {
    return normalizedCache.get(keyword)!;
  }
  
  const normalized = keyword.toLowerCase().trim();
  normalizedCache.set(keyword, normalized);
  manageCacheSize(normalizedCache);
  
  return normalized;
};

/**
 * Words that should NOT be singularized (whitelist)
 * These words are already in their correct form for microstock/SEO
 */
const SINGULARIZATION_WHITELIST = new Set([
  // Always plural words (no singular form exists or commonly used)
  'glasses', 'sunglasses', 'eyeglasses', 'binoculars', 'scissors', 'pants', 'jeans', 'shorts',
  'series', 'news', 'mathematics', 'physics', 'economics', 'politics', 'statistics',
  'species', 'means', 'headquarters', 'barracks', 'crossroads', 'works', 'thanks',
  
  // Photography/Media specific
  'lens', 'camera', 'equipment', 'gear', 'accessories', 'tools', 'supplies',
  
  // Common words that are better kept as-is
  'clothes', 'goods', 'belongings', 'surroundings', 'earnings', 'savings',
  'findings', 'proceedings', 'beginnings', 'endings', 'doings', 'goings',
  
  // Technical terms
  'data', 'criteria', 'phenomena', 'bacteria', 'media', 'strata', 'memoranda',
  'agenda', 'corpora', 'curricula', 'formulae', 'vertebrae', 'algae', 'fungi',
  
  // Proper nouns and brands (case-sensitive)
  'United States', 'United Nations', 'United Kingdom'
]);

/**
 * Convert plural to singular (improved implementation with whitelist)
 * Preserves words that should remain plural for better SEO/microstock optimization
 * Uses caching for performance optimization
 */
export const singularize = (word: string): string => {
  if (singularizedCache.has(word)) {
    return singularizedCache.get(word)!;
  }
  
  const normalized = word.toLowerCase();
  
  // Check whitelist first - don't singularize these words
  if (SINGULARIZATION_WHITELIST.has(normalized) || SINGULARIZATION_WHITELIST.has(word)) {
    singularizedCache.set(word, word);
    manageCacheSize(singularizedCache);
    return word; // Return original case
  }
  
  let result: string;
  
  // Handle special cases first
  if (normalized.endsWith('ves')) {
    result = normalized.slice(0, -3) + 'f';
  }
  // Handle common -es endings that should only remove 's'
  else if (normalized.endsWith('ses') || normalized.endsWith('ches') || normalized.endsWith('shes') || 
      normalized.endsWith('xes') || normalized.endsWith('zes')) {
    result = normalized.slice(0, -2);
  }
  // Handle -ies endings
  else if (normalized.endsWith('ies')) {
    result = normalized.slice(0, -3) + 'y';
  }
  // Default: just remove 's' (including words ending in 'es' like 'wines')
  else if (normalized.endsWith('s') && !normalized.endsWith('ss')) {
    result = normalized.slice(0, -1);
  }
  else {
    result = normalized;
  }
  
  singularizedCache.set(word, result);
  manageCacheSize(singularizedCache);
  
  return result;
};

/**
 * Split camelCase words into individual words
 * Example: "camelCase" -> ["camel", "case"]
 */
const splitCamelCase = (word: string): string[] => {
  // Handle camelCase: insert space before uppercase letters
  const camelCaseSplit = word.replace(/([a-z])([A-Z])/g, '$1 $2');
  
  // Handle PascalCase: insert space before uppercase letters (except first)
  const pascalCaseSplit = camelCaseSplit.replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
  
  // Split by spaces and filter empty strings
  return pascalCaseSplit.split(/\s+/).filter(part => part.length > 0);
};

/**
 * Split compound keywords into individual words
 * Supports: space-separated, camelCase, PascalCase, kebab-case, snake_case
 * Uses caching for performance optimization
 */
export const splitCompound = (keyword: string): string[] => {
  if (splitCompoundCache.has(keyword)) {
    return splitCompoundCache.get(keyword)!;
  }
  
  // First, split by common separators (space, hyphen, underscore)
  const parts = keyword.split(/[\s\-_]+/).filter(part => part.length > 0);
  
  // Then split each part for camelCase/PascalCase
  const allWords: string[] = [];
  
  parts.forEach(part => {
    if (part.length === 0) return;
    
    // Check if part contains camelCase or PascalCase
    const hasCamelCase = /[a-z][A-Z]/.test(part) || /[A-Z][A-Z][a-z]/.test(part);
    
    if (hasCamelCase) {
      // Split camelCase/PascalCase
      const camelWords = splitCamelCase(part);
      allWords.push(...camelWords);
    } else {
      // Keep as is (already split by separators)
      allWords.push(part);
    }
  });
  
  // Convert to lowercase and filter empty strings
  const result = allWords
    .map(word => word.toLowerCase())
    .filter(word => word.length > 0);
  
  splitCompoundCache.set(keyword, result);
  manageCacheSize(splitCompoundCache);
  
  return result;
};

/**
 * Sanitize keyword by removing unwanted punctuation
 */
export const sanitizeKeyword = (keyword: string): string => {
  return keyword
    // Remove all punctuation except hyphen and apostrophe
    .replace(/[^a-zA-Z0-9\s\-']/g, '')
    // Clean up multiple spaces
    .replace(/\s+/g, ' ')
    // Trim whitespace
    .trim()
    // Remove leading/trailing hyphens and apostrophes
    .replace(/^[-']+|[-']+$/g, '');
};

/**
 * Check if a word is a stop word
 */
export const isStopWord = (word: string): boolean => {
  return STOP_WORDS.has(word.toLowerCase());
};

/**
 * Calculate similarity between two keywords based on their relationship type
 * Uses caching for performance optimization and early exit for low similarity
 */
const calculateSimilarity = (
  keyword1: string, 
  keyword2: string, 
  type: DuplicateGroup['type']
): number => {
  // Check cache first
  const cacheKey = `${keyword1}|${keyword2}|${type}`;
  if (similarityCache.has(cacheKey)) {
    return similarityCache.get(cacheKey)!;
  }
  
  // Early exit for very different lengths (performance optimization)
  const lengthDiff = Math.abs(keyword1.length - keyword2.length);
  const maxLength = Math.max(keyword1.length, keyword2.length);
  if (maxLength > 0 && lengthDiff / maxLength > 0.8) {
    const similarity = 0.1; // Very low similarity
    similarityCache.set(cacheKey, similarity);
    manageCacheSize(similarityCache);
    return similarity;
  }
  
  const words1 = splitCompound(keyword1);
  const words2 = splitCompound(keyword2);

  let similarity: number;

  switch (type) {
    case 'exact':
      similarity = 1.0; // Perfect match
      break;
    
    case 'plural':
      similarity = 0.95; // Very high similarity
      break;
    
    case 'contains':
      // Calculate overlap ratio
      const commonWords = words1.filter(word => words2.includes(word));
      const totalWords = new Set([...words1, ...words2]).size;
      similarity = totalWords > 0 ? commonWords.length / totalWords : 0;
      break;
    
    case 'compound':
      // Calculate word overlap ratio
      const compoundCommon = words1.filter(word => words2.includes(word));
      const compoundTotal = new Set([...words1, ...words2]).size;
      similarity = compoundTotal > 0 ? compoundCommon.length / compoundTotal : 0;
      break;
    
    default:
      similarity = 0.5; // Default similarity
  }
  
  // Cache the result
  similarityCache.set(cacheKey, similarity);
  manageCacheSize(similarityCache);
  
  return similarity;
};

/**
 * Detect duplicate keywords with sophisticated logic and proper confidence scoring
 * @param keywords Array of keywords to analyze
 * @param strictMode If true, uses stricter similarity thresholds
 */
export const detectDuplicates = (keywords: string[], strictMode: boolean = false): DuplicateGroup[] => {
  const groups: DuplicateGroup[] = [];
  const allDuplicates = new Set<number>();

  // Create a matrix to track relationships and their types
  const relationships: { [key: string]: { type: DuplicateGroup['type'], similarity: number } } = {};

  // Find all pairwise relationships
  for (let i = 0; i < keywords.length; i++) {
    const currentKeyword = keywords[i];
    const currentNormalized = normalizeKeyword(currentKeyword);
    const currentSingular = singularize(currentKeyword);
    const currentWords = splitCompound(currentKeyword);
    const currentWordsNormalized = currentWords.map(word => normalizeKeyword(word));

    for (let j = i + 1; j < keywords.length; j++) {
      const compareKeyword = keywords[j];
      const compareNormalized = normalizeKeyword(compareKeyword);
      const compareSingular = singularize(compareKeyword);
      const compareWords = splitCompound(compareKeyword);
      const compareWordsNormalized = compareWords.map(word => normalizeKeyword(word));

      let isDuplicate = false;
      let duplicateType: DuplicateGroup['type'] = 'exact';

      // 1. Exact match: "Book" and "book"
      if (currentNormalized === compareNormalized) {
        isDuplicate = true;
        duplicateType = 'exact';
      }
      // 2. Plural match: "books" and "book"
      else if (currentSingular === compareSingular) {
        isDuplicate = true;
        duplicateType = 'plural';
      }
      // 3. Compound overlap: "big book" and "thick book" (both contain "book")
      // OR compound contains all words from single keyword: "iconic lace design" contains "iconic", "lace", "design"
      else if (
        // Case 1: Both are compound and share words (normalized comparison)
        (currentWords.length > 1 && 
         compareWords.length > 1 && 
         currentWordsNormalized.some(word => compareWordsNormalized.includes(word))) ||
        // Case 2: Compound contains single keyword (normalized or singular)
        (currentWords.length > 1 && 
         compareWords.length === 1 && 
         (currentWordsNormalized.includes(compareNormalized) || currentWordsNormalized.includes(compareSingular))) ||
        // Case 3: Single keyword is contained in compound (normalized or singular)
        (currentWords.length === 1 && 
         compareWords.length > 1 && 
         (compareWordsNormalized.includes(currentNormalized) || compareWordsNormalized.includes(currentSingular)))
      ) {
        isDuplicate = true;
        duplicateType = 'compound';
      }
      // 3. Contains match: "Big book" and "book" (including plurals)
      // Only for true contains where compound is significantly longer than single word
      else if (
        // Compound contains single (normalized) - compound must be significantly longer
        (currentWords.length > 2 && currentWordsNormalized.includes(compareNormalized) && compareWords.length === 1) ||
        (compareWords.length > 2 && compareWordsNormalized.includes(currentNormalized) && currentWords.length === 1) ||
        // Compound contains single (singular form) - compound must be significantly longer
        (currentWords.length > 2 && currentWordsNormalized.includes(compareSingular) && compareWords.length === 1) ||
        (compareWords.length > 2 && compareWordsNormalized.includes(currentSingular) && currentWords.length === 1)
      ) {
        isDuplicate = true;
        duplicateType = 'contains';
      }

      if (isDuplicate) {
        const similarity = calculateSimilarity(currentKeyword, compareKeyword, duplicateType);
        
        // Early exit for very low similarity (performance optimization)
        if (similarity < PERFORMANCE_CONFIG.EARLY_EXIT_THRESHOLD) {
          continue;
        }
        
        // Apply strict mode thresholds
        const minSimilarity = strictMode ? 0.7 : (duplicateType === 'compound' ? 0.3 : 0.5); // Lower threshold for compound overlap
        
        if (similarity >= minSimilarity) {
          const key = `${i}-${j}`;
          relationships[key] = { type: duplicateType, similarity };
          allDuplicates.add(i);
          allDuplicates.add(j);
        }
      }
    }
  }

  // Group connected keywords using Union-Find-like approach
  const visited = new Set<number>();
  
  for (const index of allDuplicates) {
    if (visited.has(index)) continue;

    // Find all connected keywords
    const group: number[] = [];
    const stack = [index];
    
    while (stack.length > 0) {
      const current = stack.pop()!;
      if (visited.has(current)) continue;
      
      visited.add(current);
      group.push(current);
      
      // Add all connected keywords to stack
      for (let i = 0; i < keywords.length; i++) {
        if (!visited.has(i)) {
          const key1 = `${current}-${i}`;
          const key2 = `${i}-${current}`;
          if (relationships[key1] || relationships[key2]) {
            stack.push(i);
          }
        }
      }
    }

    if (group.length > 1) {
      group.sort((a, b) => a - b); // Sort indices for consistency
      const groupKeywords = group.map(i => keywords[i]);
      
      // Calculate group confidence and determine dominant type
      let totalSimilarity = 0;
      let similarityCount = 0;
      const typeCounts: { [key in DuplicateGroup['type']]: number } = {
        exact: 0,
        plural: 0,
        contains: 0,
        compound: 0
      };

      // Analyze all pairwise relationships in this group
      for (let i = 0; i < group.length; i++) {
        for (let j = i + 1; j < group.length; j++) {
          const key1 = `${group[i]}-${group[j]}`;
          const key2 = `${group[j]}-${group[i]}`;
          const rel = relationships[key1] || relationships[key2];
          
          if (rel) {
            totalSimilarity += rel.similarity;
            similarityCount++;
            typeCounts[rel.type]++;
          }
        }
      }

      // Determine dominant type
      const dominantType = Object.entries(typeCounts)
        .reduce((a, b) => typeCounts[a[0] as DuplicateGroup['type']] > typeCounts[b[0] as DuplicateGroup['type']] ? a : b)[0] as DuplicateGroup['type'];

      // Calculate average confidence
      const confidence = similarityCount > 0 ? totalSimilarity / similarityCount : 0.5;

      groups.push({
        indices: group,
        keywords: groupKeywords,
        type: dominantType,
        confidence: Math.round(confidence * 100) / 100 // Round to 2 decimal places
      });
    }
  }

  return groups;
};

/**
 * Check if a keyword index is part of any duplicate group
 */
export const isDuplicateKeyword = (index: number, duplicateGroups: DuplicateGroup[]): boolean => {
  return duplicateGroups.some(group => group.indices.includes(index));
};

/**
 * Get total duplicate count from duplicate groups
 */
export const getTotalDuplicates = (duplicateGroups: DuplicateGroup[]): number => {
  return duplicateGroups.reduce((total, group) => total + group.indices.length, 0);
};

/**
 * Auto-fix duplicates using microstock optimization strategy
 */
export const autoFixKeywords = (
  keywords: string[], 
  options: AutoFixOptions = {}
): AutoFixResult => {
  const {
    maxKeywords = 100,
    strictMode = false,
    filterStopWords = true
  } = options;

  const duplicateGroups = detectDuplicates(keywords, strictMode);
  const indicesToRemove = new Set<number>();
  const wordsToAdd = new Set<string>();

  // Process each duplicate group
  for (const group of duplicateGroups) {
    const { indices, keywords: groupKeywords } = group;
    
    // Strategy: Extract all unique single words from all keywords in the group
    // Remove all original keywords, add only the single words
    
    // Collect all individual words from all keywords in this group
    const allWords = new Set<string>();
    
    groupKeywords.forEach(keyword => {
      const words = splitCompound(keyword);
      words.forEach(word => {
        // Only keep meaningful words (length > 1 to avoid single letters)
        if (word.length > 1) {
          // Filter out stop words if enabled
          if (filterStopWords && isStopWord(word)) {
            return; // Skip this word
          }
          
          // Singularize each word for microstock optimization
          const singularWord = singularize(word);
          allWords.add(singularWord);
        }
      });
    });
    
    // Mark all original keywords for removal
    indices.forEach(index => {
      indicesToRemove.add(index);
    });
    
    // Add the extracted single words
    allWords.forEach(word => {
      wordsToAdd.add(word);
    });
  }

  // Process non-duplicate keywords that contain stop words
  if (filterStopWords) {
    keywords.forEach((keyword, index) => {
      // Skip if already marked for removal
      if (indicesToRemove.has(index)) return;
      
      const words = splitCompound(keyword);
      const hasStopWords = words.some(word => isStopWord(word));
      
      // If keyword contains stop words, split it into individual words
      if (hasStopWords) {
        const cleanWords = words
          .filter(word => word.length > 1 && !isStopWord(word))
          .map(word => singularize(word));
        
        if (cleanWords.length > 0) {
          // Mark original keyword for removal
          indicesToRemove.add(index);
          
          // Add clean words
          cleanWords.forEach(word => wordsToAdd.add(word));
        }
      }
    });
  }

  // Create new keywords array
  let fixedKeywords = [...keywords];
  
  // Remove duplicates (in reverse order to maintain indices)
  const sortedIndicesToRemove = Array.from(indicesToRemove).sort((a, b) => b - a);
  sortedIndicesToRemove.forEach(index => {
    fixedKeywords.splice(index, 1);
  });

  // Add new single words, but filter out any that already exist
  const existingNormalized = fixedKeywords.map(k => normalizeKeyword(k));
  const uniqueNewWords = Array.from(wordsToAdd).filter(word => 
    !existingNormalized.includes(normalizeKeyword(word))
  );
  
  fixedKeywords.push(...uniqueNewWords);

  // Enforce keyword limit - remove from the END to preserve most important keywords
  // AI engine provides keywords in order: most important → less important
  const removedCount = Math.max(0, fixedKeywords.length - maxKeywords);
  if (fixedKeywords.length > maxKeywords) {
    const removedKeywords = fixedKeywords.slice(maxKeywords);
    fixedKeywords = fixedKeywords.slice(0, maxKeywords);
    
    // Auto-fix: Removed least important keywords for optimal SEO
  }

  return {
    fixedKeywords,
    removedCount: sortedIndicesToRemove.length + removedCount,
    addedCount: uniqueNewWords.length,
    duplicateGroups
  };
};

/**
 * Advanced keyword actions
 */

/**
 * Randomize keyword order
 */
export const shuffleKeywords = (keywords: string[]): string[] => {
  return [...keywords].sort(() => Math.random() - 0.5);
};

/**
 * Remove plural keywords, keep only singular forms
 */
export const removePlurals = (keywords: string[]): string[] => {
  return keywords.filter(keyword => {
    // For compound keywords, check if any word needs singularization
    const words = splitCompound(keyword);
    
    // If it's a single word, use the original logic
    if (words.length === 1) {
      const singular = singularize(keyword);
      const normalized = normalizeKeyword(keyword);
      return singular === normalized;
    }
    
    // For compound keywords, check if any word would change after singularization
    // If any word changes, it means there are plurals to remove
    const hasPlurals = words.some(word => {
      const singular = singularize(word);
      const normalized = normalizeKeyword(word);
      return singular !== normalized;
    });
    
    // Keep if no plurals found (all words are already singular or whitelisted)
    return !hasPlurals;
  });
};

/**
 * Split compound keywords into single words
 */
export const splitToSingleWords = (keywords: string[], maxKeywords: number = 100, filterStopWords: boolean = true): string[] => {
  const allSingleWords = new Set<string>();
  
  keywords.forEach(keyword => {
    const words = splitCompound(keyword);
    words.forEach(word => {
      if (word.length > 1) {
        // Filter out stop words if enabled
        if (filterStopWords && isStopWord(word)) {
          return; // Skip this word
        }
        
        const singularWord = singularize(word);
        allSingleWords.add(singularWord);
      }
    });
  });
  
  let result = Array.from(allSingleWords);
  
  // Enforce limit - keep most important (first ones)
  if (result.length > maxKeywords) {
    result = result.slice(0, maxKeywords);
  }
  
  return result;
};

/**
 * Remove multi-word keywords, keep only single words
 */
export const removeMultiWordKeywords = (keywords: string[]): string[] => {
  return keywords.filter(keyword => {
    const words = splitCompound(keyword);
    return words.length === 1; // Keep only single words
  });
};

/**
 * Trim keywords to specified count
 */
export const trimKeywords = (keywords: string[], targetCount: number): string[] => {
  if (targetCount < 1 || targetCount >= keywords.length) {
    return keywords; // No trimming needed
  }
  
  return keywords.slice(0, targetCount);
};

/**
 * Find and replace keywords
 */
export const findAndReplaceKeywords = (
  keywords: string[], 
  find: string, 
  replace: string, 
  wholeKeyword: boolean = false
): string[] => {
  if (!find.trim()) return keywords;
  
  const findLower = find.toLowerCase();
  
  return keywords.map(keyword => {
    if (wholeKeyword) {
      // Match whole keyword only
      if (normalizeKeyword(keyword) === findLower) {
        return replace;
      }
      return keyword;
    } else {
      // Match word within keyword
      const words = splitCompound(keyword);
      const replacedWords = words.map(word => {
        if (normalizeKeyword(word) === findLower) {
          return replace;
        }
        return word;
      });
      return replacedWords.join(' ');
    }
  });
};

/**
 * Find and delete keywords
 */
export const findAndDeleteKeywords = (
  keywords: string[], 
  keywordsToDelete: string[]
): string[] => {
  if (keywordsToDelete.length === 0) return keywords;
  
  return keywords.filter(keyword => {
    const normalizedKeyword = normalizeKeyword(keyword);
    return !keywordsToDelete.some(deleteKeyword => 
      normalizedKeyword === normalizeKeyword(deleteKeyword)
    );
  });
};

/**
 * Parse comma-separated keywords input
 */
export const parseKeywordsInput = (
  input: string, 
  maxKeywords: number = 100, 
  maxKeywordLength: number = 80
): string[] => {
  return input
    .split(',')
    .map(k => k.trim())
    .map(k => sanitizeKeyword(k))  // Clean unwanted punctuation
    .filter(k => k.length > 0)
    .filter(k => k.length <= maxKeywordLength)
    .slice(0, maxKeywords);
};

/**
 * Check for duplicates in new keywords against existing keywords
 */
export const checkDuplicates = (newKeywords: string[], existingKeywords: string[]): string[] => {
  const duplicates: string[] = [];
  const existingNormalized = existingKeywords.map(k => normalizeKeyword(k));
  
  newKeywords.forEach(keyword => {
    const normalizedKeyword = normalizeKeyword(keyword);
    if (existingNormalized.includes(normalizedKeyword)) {
      // Find the original keyword (with original case)
      const originalKeyword = existingKeywords.find(k => normalizeKeyword(k) === normalizedKeyword);
      if (originalKeyword) {
        duplicates.push(originalKeyword);
      }
    }
  });
  
  return duplicates;
};

export default {
  // Core functions
  autoFixKeywords,
  detectDuplicates,
  
  // Helper functions
  normalizeKeyword,
  singularize,
  splitCompound,
  sanitizeKeyword,
  isStopWord,
  isDuplicateKeyword,
  getTotalDuplicates,
  
  // Advanced actions
  shuffleKeywords,
  removePlurals,
  splitToSingleWords,
  removeMultiWordKeywords,
  trimKeywords,
  findAndReplaceKeywords,
  findAndDeleteKeywords,
  
  // Utility functions
  parseKeywordsInput,
  checkDuplicates
};
